/*
 * Playback for the product-video panel under the product card.
 *
 * The player is rendered inline and laid out on every page load — that is what
 * Google's video pipeline needs to see — so there is nothing to "load" here.
 * This element only swaps the themed overlay for the player, and puts a status
 * banner up when the CDN drops the stream.
 *
 * It used to live in media-gallery.js, back when the video was a gallery slide.
 */
if (!customElements.get('qubzi-product-video')) {
  customElements.define(
    'qubzi-product-video',
    class QubziProductVideo extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', this.onPosterClick.bind(this));
        // `error` and `playing` do not bubble, so both listen in the capture
        // phase — that is the only way one listener on this element sees them.
        this.addEventListener('error', this.onVideoError.bind(this), true);
        this.addEventListener('playing', this.onVideoPlaying.bind(this), true);
      }

      onPosterClick(event) {
        const poster = event.target.closest('[data-bferp-video] [id^="Deferred-Poster-"]');
        if (!poster) return;
        const player = poster.closest('[data-bferp-video]');
        // The player is already in the DOM. Play it synchronously: deferring to
        // requestAnimationFrame left the video stuck on its poster whenever the
        // tab was hidden, because hidden documents never run animation frames.
        const video = player.querySelector('video');
        if (!video) return;
        // Anything else playing on the page — a gallery video, a 3D model —
        // stops first. Called before play(), so it never cancels this one.
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        // The overlay paints on top of the player and is what was just clicked.
        // Uncover the player and hand it the tab stop the overlay was holding.
        player.setAttribute('data-bferp-playing', '');
        video.removeAttribute('tabindex');
        // A fatal media error parks the element at NETWORK_NO_SOURCE, where
        // play() rejects instead of re-fetching, so a retry has to reset the
        // element or the shopper's second click can never recover from a
        // dropped connection.
        if (player.hasAttribute('data-bferp-video-failed')) {
          video.load();
          // The banner still reads "unavailable" from the previous attempt.
          // This retry is under way, so clear it now and let onVideoError put
          // it back if this attempt fails too — otherwise it sits over a video
          // that is playing perfectly well.
          player.removeAttribute('data-bferp-video-failed');
          const staleError = this.errorBanner(player);
          if (staleError) staleError.hidden = true;
        }
        // The overlay is being hidden while it still holds focus, so move focus
        // onto the player rather than dropping it back to the document body.
        video.focus({ preventScroll: true });
        video.muted = true;
        const playRequest = video.play();
        if (playRequest) {
          playRequest.catch((error) => {
            // pauseAllMedia elsewhere on the page rejects a pending play() with
            // AbortError, and an autoplay policy rejects with NotAllowedError.
            // Neither means the video is broken, and treating them as fatal
            // would tell the shopper a working video is unavailable.
            const name = error && error.name;
            if (name === 'AbortError' || name === 'NotAllowedError') return;
            this.showVideoError(player);
          });
        }
      }

      onVideoError(event) {
        // This listener captures every error event in the panel, so it also
        // sees a <track> whose captions 404. That does not break playback, and
        // tearing the player down for it tells the shopper a working video is
        // unavailable. Only the media element itself reports a real failure.
        const target = event.target;
        const tag = target && target.tagName;
        if (tag !== 'VIDEO' && tag !== 'SOURCE') return;
        const player = target.closest?.('[data-bferp-video]');
        if (player) this.showVideoError(player);
      }

      // The banner is a sibling of the player rather than a child, because Dawn
      // paints every direct child of `.media` as a full-bleed absolute block and
      // that would defeat its `hidden` attribute. Resolve it by the id the
      // snippet records, and fall back to the surrounding stage.
      errorBanner(player) {
        const errorId = player.dataset.bferpVideoErrorId;
        const byId = errorId ? document.getElementById(errorId) : null;
        if (byId) return byId;
        const stage = player.closest('.qubzi-product-video__stage');
        return stage ? stage.querySelector('[data-bferp-video-error]') : null;
      }

      onVideoPlaying(event) {
        const player = event.target.closest?.('[data-bferp-video]');
        if (!player) return;
        // Safety net for any path that starts playback without going through
        // the overlay click: a playing video must never stay hidden behind it.
        player.setAttribute('data-bferp-playing', '');
        player.removeAttribute('data-bferp-video-failed');
        const video = event.target;
        if (video.tagName === 'VIDEO') video.removeAttribute('tabindex');
        const error = this.errorBanner(player);
        if (error) error.hidden = true;
      }

      // A failure must not remove the player: nothing would put it back, the
      // page would lose the element Google indexes, and a retry click would have
      // nothing left to play. Pause it and bring the overlay back instead.
      showVideoError(player) {
        const video = player.querySelector('video');
        if (video) {
          video.pause();
          video.setAttribute('tabindex', '-1');
        }
        player.removeAttribute('data-bferp-playing');
        player.setAttribute('data-bferp-video-failed', '');
        const error = this.errorBanner(player);
        if (error) {
          error.hidden = false;
          error.textContent = player.dataset.bferpVideoErrorMessage || error.textContent;
        }
      }
    }
  );
}
